import { confirmDialog, promptDialog } from "./confirmDialog";
import { ensureLedgerMarkIndex, normalizeAccountShortName } from "./ledgerMarks";
import {
  createLedgerAccount,
  createLedgerBroker,
  deleteLedgerAccount,
  deleteLedgerBroker,
  loadLedgerCatalog,
  renameLedgerAccount,
  renameLedgerBroker,
  type LedgerAccount,
  type LedgerBroker,
} from "./ownLedgerClient";

export interface LedgerCatalogState {
  brokers: LedgerBroker[];
  accounts: LedgerAccount[];
}

export type LedgerCatalogScope = "all" | "robinhood" | "other";

export function isRobinhoodLedgerBroker(broker: Pick<LedgerBroker, "broker_id" | "name">): boolean {
  const id = String(broker.broker_id || "").trim().toLowerCase();
  const name = String(broker.name || "").trim().toLowerCase();
  return id === "robinhood" || name === "robinhood";
}

function scopedLedgerCatalog(
  catalog: LedgerCatalogState | null | undefined,
  scope: LedgerCatalogScope,
): LedgerCatalogState {
  const visible = visibleLedgerCatalog(catalog);
  if (scope === "all") return visible;
  const brokers = visible.brokers.filter((broker) =>
    scope === "robinhood" ? isRobinhoodLedgerBroker(broker) : !isRobinhoodLedgerBroker(broker),
  );
  const ids = new Set(brokers.map((broker) => broker.broker_id));
  return {
    brokers,
    accounts: visible.accounts.filter((account) => ids.has(account.broker_id)),
  };
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function emptyLedgerCatalog(): LedgerCatalogState {
  return { brokers: [], accounts: [] };
}

function isHiddenCatalogBroker(brokerId: string | null | undefined, name?: string | null): boolean {
  const id = String(brokerId || "").trim().toLowerCase();
  const label = String(name || "").trim().toLowerCase();
  return id === "undefined" || label === "undefined";
}

function isHiddenCatalogAccount(account: LedgerAccount): boolean {
  const id = String(account.account_id || "").trim().toLowerCase();
  return (
    id === "undefined:undefined" ||
    id.startsWith("undefined:") ||
    isHiddenCatalogBroker(account.broker_id, account.label)
  );
}

function visibleLedgerCatalog(catalog: LedgerCatalogState | null | undefined): LedgerCatalogState {
  const brokers = (catalog?.brokers ?? []).filter(
    (broker) => !isHiddenCatalogBroker(broker.broker_id, broker.name),
  );
  const visibleBrokerIds = new Set(brokers.map((broker) => broker.broker_id));
  const accounts = (catalog?.accounts ?? []).filter(
    (account) => !isHiddenCatalogAccount(account) && visibleBrokerIds.has(account.broker_id),
  );
  return { brokers, accounts };
}

export function renderLedgerBrokersSettingsHtml(
  catalog: LedgerCatalogState | null,
  opts: {
    loading?: boolean;
    error?: string;
    variant?: "web" | "mobile";
    scope?: LedgerCatalogScope;
  } = {},
): string {
  const variant = opts.variant ?? "web";
  const scope = opts.scope ?? "all";
  const lead =
    scope === "robinhood"
      ? "Names shown in History and Analytics for your Robinhood accounts. Short name is the narrow code on History, Puts, and Calls. It starts from the account name — change it here."
      : "Brokers and accounts are lookup tables. Short name is the narrow code on History, Puts, and Calls. It starts from the account name — change it here. Rename a broker or account and every linked row updates.";
  if (opts.loading && !catalog) {
    return variant === "mobile"
      ? `<p class="sfe-mobile-status">Loading brokers and accounts…</p>`
      : `<p class="muted small sfe-column-settings-lead">Loading brokers and accounts…</p>`;
  }
  const { brokers, accounts } = scopedLedgerCatalog(catalog, scope);
  const rows = brokers
    .map((broker) => {
      const brokerAccounts = accounts.filter((account) => account.broker_id === broker.broker_id);
      const accountRows = brokerAccounts.length
        ? brokerAccounts
            .map(
              (account) => {
              const shortName = normalizeAccountShortName(account.short_name);
              return `
                <li class="sfe-ledger-catalog-account" data-account-id="${esc(account.account_id)}">
                  <span class="sfe-ledger-catalog-name">${esc(account.label)}</span>
                  <label class="sfe-ledger-catalog-short">
                    <span>Short</span>
                    <input
                      type="text"
                      maxlength="6"
                      spellcheck="false"
                      autocapitalize="characters"
                      data-ledger-short-name
                      data-account-id="${esc(account.account_id)}"
                      data-saved-short="${esc(shortName)}"
                      value="${esc(shortName)}"
                      aria-label="Short name for ${esc(account.label)}"
                    />
                  </label>
                  <span class="sfe-ledger-catalog-actions">
                    <button type="button" class="small ghost" data-ledger-action="rename-account" data-account-id="${esc(account.account_id)}" data-account-name="${esc(account.label)}">Rename</button>
                    <button type="button" class="small ghost" data-ledger-action="delete-account" data-account-id="${esc(account.account_id)}" data-account-name="${esc(account.label)}">Remove</button>
                  </span>
                </li>`;
            })
            .join("")
        : `<li class="sfe-ledger-catalog-empty muted small">No accounts yet.</li>`;
      const brokerActions =
        scope === "robinhood"
          ? `<span class="sfe-ledger-catalog-actions">
              <button type="button" class="small ghost" data-ledger-action="rename-broker" data-broker-id="${esc(broker.broker_id)}" data-broker-name="${esc(broker.name)}">Rename</button>
            </span>`
          : `<span class="sfe-ledger-catalog-actions">
              <button type="button" class="small ghost" data-ledger-action="rename-broker" data-broker-id="${esc(broker.broker_id)}" data-broker-name="${esc(broker.name)}">Rename</button>
              <button type="button" class="small ghost" data-ledger-action="delete-broker" data-broker-id="${esc(broker.broker_id)}" data-broker-name="${esc(broker.name)}">Remove</button>
            </span>`;
      return `
        <article class="sfe-ledger-catalog-broker" data-broker-id="${esc(broker.broker_id)}">
          <div class="sfe-ledger-catalog-broker-head">
            <strong class="sfe-ledger-catalog-name">${esc(broker.name)}</strong>
            ${brokerActions}
          </div>
          <ul class="sfe-ledger-catalog-accounts">${accountRows}</ul>
        </article>`;
    })
    .join("");
  const brokerOptions = brokers
    .map((broker) => `<option value="${esc(broker.broker_id)}">${esc(broker.name)}</option>`)
    .join("");
  const error = opts.error
    ? `<p class="muted small sfe-ledger-catalog-error" role="alert">${esc(opts.error)}</p>`
    : "";
  return `
    <p class="${variant === "mobile" ? "sfe-mobile-status" : "muted small sfe-column-settings-lead"}">${esc(lead)}</p>
    <div class="sfe-ledger-catalog-list">${rows || `<p class="muted small">${scope === "robinhood" ? "No Robinhood accounts yet. They appear after you connect or import." : "No brokers yet."}</p>`}</div>
    ${
      scope === "robinhood"
        ? ""
        : `<div class="sfe-ledger-catalog-add">
      <label class="${variant === "mobile" ? "sfe-mobile-settings-field" : "sfe-modal-field sfe-web-settings-field"}">
        <span>New broker</span>
        <span class="sfe-ledger-catalog-inline">
          <input type="text" data-ledger-new-broker placeholder="Broker name" maxlength="64" />
          <button type="button" class="small" data-ledger-action="add-broker">Add</button>
        </span>
      </label>
      <label class="${variant === "mobile" ? "sfe-mobile-settings-field" : "sfe-modal-field sfe-web-settings-field"}">
        <span>New account</span>
        <span class="sfe-ledger-catalog-inline">
          <select data-ledger-new-account-broker>${brokerOptions || `<option value="">Add a broker first</option>`}</select>
          <input type="text" data-ledger-new-account placeholder="Account name" maxlength="64" />
          <button type="button" class="small" data-ledger-action="add-account"${brokers.length ? "" : " disabled"}>Add</button>
        </span>
      </label>
    </div>`
    }
    ${error}`;
}

export async function bindLedgerBrokersSettings(
  host: HTMLElement,
  opts: {
    variant?: "web" | "mobile";
    scope?: LedgerCatalogScope;
    onChange?: (catalog: LedgerCatalogState) => void;
    onError?: (message: string) => void;
  } = {},
): Promise<() => void> {
  let catalog: LedgerCatalogState | null = null;
  let error = "";
  let disposed = false;

  const paint = (loading = false) => {
    if (disposed) return;
    host.innerHTML = renderLedgerBrokersSettingsHtml(catalog, {
      loading,
      error,
      variant: opts.variant,
      scope: opts.scope,
    });
  };

  const reload = async () => {
    error = "";
    paint(true);
    try {
      catalog = await loadLedgerCatalog(true);
      opts.onChange?.(catalog);
      void ensureLedgerMarkIndex(true);
    } catch (err) {
      error = err instanceof Error ? err.message : "Failed to load brokers and accounts";
      opts.onError?.(error);
    }
    paint();
  };

  const handleClick = (event: Event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-ledger-action]");
    if (!button || !host.contains(button)) return;
    const action = button.dataset.ledgerAction;
    if (!action) return;
    event.preventDefault();
    void (async () => {
      try {
        if (action === "add-broker") {
          const input = host.querySelector<HTMLInputElement>("[data-ledger-new-broker]");
          const name = input?.value.trim() ?? "";
          if (!name) {
            input?.focus();
            return;
          }
          await createLedgerBroker(name);
          if (input) input.value = "";
        } else if (action === "rename-broker") {
          const next = await promptDialog({
            title: "Rename broker",
            message: "This name appears on every transaction linked to this broker.",
            defaultValue: button.dataset.brokerName ?? "",
            confirmLabel: "Rename",
          });
          if (!next || !button.dataset.brokerId) return;
          await renameLedgerBroker(button.dataset.brokerId, next);
        } else if (action === "delete-broker") {
          const ok = await confirmDialog({
            title: "Remove broker?",
            message: `Remove ${button.dataset.brokerName ?? "this broker"}? Unused accounts under it are removed too. Accounts that still have transactions stay.`,
            confirmLabel: "Remove",
            danger: true,
          });
          if (!ok || !button.dataset.brokerId) return;
          const brokerId = button.dataset.brokerId;
          if (catalog) {
            catalog = {
              brokers: catalog.brokers.filter((row) => row.broker_id !== brokerId),
              accounts: catalog.accounts.filter((row) => row.broker_id !== brokerId),
            };
            paint();
          }
          await deleteLedgerBroker(brokerId);
        } else if (action === "add-account") {
          const nameInput = host.querySelector<HTMLInputElement>("[data-ledger-new-account]");
          const brokerSelect = host.querySelector<HTMLSelectElement>("[data-ledger-new-account-broker]");
          const name = nameInput?.value.trim() ?? "";
          const brokerId = brokerSelect?.value ?? "";
          if (!name) {
            nameInput?.focus();
            return;
          }
          if (!brokerId) return;
          await createLedgerAccount({ name, brokerId });
          if (nameInput) nameInput.value = "";
        } else if (action === "rename-account") {
          const next = await promptDialog({
            title: "Rename account",
            message: "This name appears on every transaction linked to this account.",
            defaultValue: button.dataset.accountName ?? "",
            confirmLabel: "Rename",
          });
          if (!next || !button.dataset.accountId) return;
          await renameLedgerAccount(button.dataset.accountId, { name: next });
        } else if (action === "delete-account") {
          const ok = await confirmDialog({
            title: "Remove account?",
            message: `Remove ${button.dataset.accountName ?? "this account"}? Transactions must be moved or deleted first.`,
            confirmLabel: "Remove",
            danger: true,
          });
          if (!ok || !button.dataset.accountId) return;
          const accountId = button.dataset.accountId;
          if (catalog) {
            catalog = {
              brokers: catalog.brokers,
              accounts: catalog.accounts.filter((row) => row.account_id !== accountId),
            };
            paint();
          }
          await deleteLedgerAccount(accountId);
        } else {
          return;
        }
        await reload();
      } catch (err) {
        error = err instanceof Error ? err.message : "Update failed";
        opts.onError?.(error);
        paint();
      }
    })();
  };

  const handleFocusOut = (event: FocusEvent) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches("[data-ledger-short-name]")) return;
    if (!host.contains(input)) return;
    if (input.dataset.saving === "1") return;
    const accountId = input.dataset.accountId ?? "";
    const next = normalizeAccountShortName(input.value);
    const prev = normalizeAccountShortName(input.dataset.savedShort);
    if (!accountId || !next || next === prev) {
      input.value = prev;
      return;
    }
    input.dataset.saving = "1";
    void (async () => {
      try {
        error = "";
        await renameLedgerAccount(accountId, { shortName: next });
        await reload();
      } catch (err) {
        error = err instanceof Error ? err.message : "Update failed";
        opts.onError?.(error);
        paint();
      }
    })();
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Enter") return;
    const target = event.target as HTMLElement;
    if (!host.contains(target)) return;
    if (target.matches("[data-ledger-short-name]")) {
      event.preventDefault();
      (target as HTMLInputElement).blur();
      return;
    }
    if (target.matches("[data-ledger-new-broker]")) {
      event.preventDefault();
      host.querySelector<HTMLButtonElement>("[data-ledger-action='add-broker']")?.click();
    } else if (target.matches("[data-ledger-new-account]")) {
      event.preventDefault();
      host.querySelector<HTMLButtonElement>("[data-ledger-action='add-account']")?.click();
    }
  };

  host.addEventListener("click", handleClick);
  host.addEventListener("keydown", handleKeydown);
  host.addEventListener("focusout", handleFocusOut);
  await reload();
  return () => {
    disposed = true;
    host.removeEventListener("click", handleClick);
    host.removeEventListener("keydown", handleKeydown);
    host.removeEventListener("focusout", handleFocusOut);
  };
}

export function renderRobinhoodImportTargetHtml(
  catalog: LedgerCatalogState,
  selectedBrokerId: string,
  selectedAccountId: string,
): string {
  const visible = visibleLedgerCatalog(catalog);
  const brokers = visible.brokers.filter((broker) => broker.broker_id === "robinhood");
  const accounts = visible.accounts.filter((account) => account.broker_id === "robinhood");
  const brokerOptions = brokers
    .map((broker) => {
      const selected = broker.broker_id === selectedBrokerId ? " selected" : "";
      return `<option value="${esc(broker.broker_id)}"${selected}>${esc(broker.name)}</option>`;
    })
    .join("");
  const accountOptions = accounts.length
    ? accounts
        .map((account) => {
          const selected = account.account_id === selectedAccountId ? " selected" : "";
          return `<option value="${esc(account.account_id)}"${selected}>${esc(account.label)}</option>`;
        })
        .join("")
    : `<option value="">No accounts for this broker</option>`;
  return `
    <div class="sfe-agentic-import-target">
      <label class="sfe-agentic-import-target-field">
        <span class="small">Ledger broker</span>
        <select data-rh-import-broker>${brokerOptions || `<option value="">No brokers</option>`}</select>
      </label>
      <label class="sfe-agentic-import-target-field">
        <span class="small">Ledger account</span>
        <select data-rh-import-account>${accountOptions}</select>
      </label>
      <p class="muted small sfe-agentic-import-target-help">Imported Robinhood trades and cash stay on a Robinhood account. Rename it in Settings without rewriting history rows.</p>
    </div>`;
}

export function resolveImportTargetSelection(
  catalog: LedgerCatalogState,
  brokerId: string | null | undefined,
  accountId: string | null | undefined,
): { brokerId: string; accountId: string } {
  const { brokers, accounts } = visibleLedgerCatalog(catalog);
  const robinhoodBrokers = brokers.filter((row) => row.broker_id === "robinhood");
  const robinhoodAccounts = accounts.filter((row) => row.broker_id === "robinhood");
  const broker = robinhoodBrokers[0];
  const resolvedBrokerId = broker?.broker_id ?? "robinhood";
  const account =
    robinhoodAccounts.find((row) => row.account_id === accountId) ??
    robinhoodAccounts.find((row) => row.label === "Individual") ??
    robinhoodAccounts[0];
  return {
    brokerId: resolvedBrokerId,
    accountId: account?.account_id ?? "",
  };
}
